import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, Check, Edit3, XCircle, Sparkles, Link2, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import ConstructionContextSelector from './ConstructionContextSelector';
import ConstructionBreadcrumb from './ConstructionBreadcrumb';
import EvidenceButton from './sitewalk/EvidenceButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => (v === null || v === undefined ? '—' : (typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(2)) : v));

const REVIEW_BADGE = {
  PENDING_REVIEW: 'bg-amber-100 text-amber-700',
  ACCEPTED: 'bg-emerald-100 text-emerald-700',
  CORRECTED: 'bg-blue-100 text-blue-700',
  REJECTED: 'bg-red-100 text-red-700',
};

// Slide-over detail: definition, reconciliation (with formula), material mapping, context, evidence timeline + review.
const BOQItemDrawer = ({ projectId, itemId, canReview, canMap, onClose, onChanged }) => {
  const [item, setItem] = useState(null);
  const [recon, setRecon] = useState(null);
  const [obs, setObs] = useState([]);
  const [links, setLinks] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState({});
  const [correcting, setCorrecting] = useState(null); // obsId being corrected

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [it, rc, ob, lk] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/boq-items/${itemId}`).then((r) => r.data),
        axios.get(`${API}/projects/${projectId}/boq-items/${itemId}/reconcile`).then((r) => r.data),
        axios.get(`${API}/projects/${projectId}/quantity-observations`, { params: { boqItemId: itemId } }).then((r) => r.data),
        axios.get(`${API}/projects/${projectId}/material-links`, { params: { boqItemId: itemId } }).then((r) => r.data),
      ]);
      setItem(it); setRecon(rc); setObs(ob); setLinks(lk); setCtx(it.constructionContext || {});
    } catch (e) { toast.error('Failed to load BOQ item'); } finally { setLoading(false); }
  }, [projectId, itemId]);

  useEffect(() => { load(); }, [load]);

  const loadSuggestions = async () => {
    try { setSuggestions((await axios.get(`${API}/projects/${projectId}/boq-items/${itemId}/material-suggestions`)).data); }
    catch (e) { toast.error('No suggestions'); }
  };

  const confirmMaterial = async (materialId, source) => {
    try {
      await axios.post(`${API}/projects/${projectId}/material-links`, { materialId, boqItemId: itemId, status: 'CONFIRMED', source });
      toast.success('Material mapped');
      await load(); onChanged && onChanged();
    } catch (e) { toast.error('Mapping failed'); }
  };

  const saveContext = async () => {
    try {
      await axios.post(`${API}/projects/${projectId}/boq-items/${itemId}/context`, { context: ctx });
      toast.success('Context mapped'); await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Context mapping failed'); }
  };

  const review = async (obsId, status, reviewedQuantity, reason) => {
    try {
      await axios.post(`${API}/projects/${projectId}/quantity-observations/${obsId}/review`, { status, reviewedQuantity, reason });
      toast.success(`Observation ${status.toLowerCase()}`);
      setCorrecting(null); await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); }
  };

  const buckets = recon ? [
    ['Planned', recon.planned], ['Received', recon.received], ['Visual Stock', recon.visualStock],
    ['Installed', recon.installed], ['Damaged', recon.damaged], ['Rejected', recon.rejected],
    ['Remaining', recon.remaining], ['Variance', recon.variance],
  ] : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="boq-item-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-slate-50 h-full overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-bold text-slate-900 truncate">{item ? `${item.itemCode || 'Item'} — ${item.description || ''}` : 'BOQ Item'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="boq-drawer-close"><X className="w-5 h-5" /></button>
        </div>

        {loading || !recon ? (
          <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Definition */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><p className="text-xs text-slate-400">Unit</p><p className="font-medium">{item.unit || item.rawUnit || '—'}</p></div>
                <div><p className="text-xs text-slate-400">Planned</p><p className="font-medium">{fmt(item.plannedQuantity)}</p></div>
                <div><p className="text-xs text-slate-400">Rate</p><p className="font-medium">{fmt(item.rate)}</p></div>
                <div><p className="text-xs text-slate-400">Trade</p><ConstructionBreadcrumb projectId={projectId} context={item.constructionContext} fallback="Unmapped" testId="boq-drawer-ctx" /><div className="mt-2" data-testid="boq-360-evidence"><EvidenceButton projectId={projectId} kind="boq-item" id={itemId} compact /></div></div>
              </div>
            </div>

            {/* Reconciliation */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="boq-reconciliation">
              <h3 className="font-semibold text-slate-800 mb-3">Quantity Reconciliation</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {buckets.map(([label, val]) => (
                  <div key={label} className="bg-slate-50 rounded-lg p-3" data-testid={`recon-${label.replace(/\s/g,'').toLowerCase()}`}>
                    <p className="text-lg font-bold text-slate-900">{fmt(val)}</p>
                    <p className="text-xs text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
              {recon.formulas?.remaining && (
                <p className="mt-3 text-xs text-slate-500 bg-blue-50 rounded-lg px-3 py-2" data-testid="recon-formula">{recon.formulas.remaining}</p>
              )}
              {recon.note && <p className="mt-2 text-xs text-amber-600">{recon.note}</p>}
              {recon.unreconciled?.length > 0 && (
                <p className="mt-2 text-xs text-amber-600" data-testid="recon-unreconciled">{recon.unreconciled.length} observation(s) unreconciled (pending review or incompatible units)</p>
              )}
              <p className="mt-2 text-[11px] text-slate-400">Values shown as — mean "No Data" (not zero).</p>
            </div>

            {/* Material mapping */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Link2 className="w-4 h-4" /> Material Mapping</h3>
                {canMap && <button onClick={loadSuggestions} className="text-xs text-blue-600 flex items-center gap-1" data-testid="boq-suggest-materials"><Sparkles className="w-3 h-3" /> Suggest</button>}
              </div>
              {links.length > 0 ? links.map((l) => (
                <span key={l.id} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full mr-1" data-testid="boq-material-link">{l.status}: {l.materialId.slice(0, 6)}…</span>
              )) : <p className="text-xs text-slate-400">Unmapped</p>}
              {suggestions.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="boq-material-suggestions">
                  {suggestions.map((s) => (
                    <div key={s.materialId} className="flex items-center justify-between text-sm border-t border-slate-50 pt-1">
                      <span>{s.materialName} <span className="text-xs text-slate-400">({(s.score * 100).toFixed(0)}% match)</span></span>
                      {canMap && <button onClick={() => confirmMaterial(s.materialId, 'suggested')} className="text-xs px-2 py-1 bg-blue-600 text-white rounded" data-testid="boq-confirm-material">Confirm</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Context mapping */}
            {canMap && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><MapPin className="w-4 h-4" /> Construction Context</h3>
                <ConstructionContextSelector projectId={projectId} value={ctx} onChange={setCtx} />
                <button onClick={saveContext} className="mt-3 px-3 py-2 bg-slate-800 text-white text-sm rounded-lg" data-testid="boq-save-context">Save Context</button>
              </div>
            )}

            {/* Evidence timeline */}
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="boq-evidence-timeline">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Quantity Evidence Timeline</h3>
              {obs.length === 0 ? <p className="text-sm text-slate-400">No quantity observations yet.</p> : (
                <div className="space-y-3">
                  {obs.map((o) => (
                    <div key={o.id} className="border-l-2 border-slate-200 pl-3" data-testid="boq-observation">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-400">{(o.captureDate || '').slice(0, 10)}</span>
                        <span className="text-sm font-medium">{o.observationType}</span>
                        <span className="text-xs text-slate-500">{o.sourceType}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${REVIEW_BADGE[o.reviewStatus]}`}>{o.reviewStatus}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1">
                        Observed <b>{fmt(o.observedQuantity)}</b> {o.unit || o.rawUnit || ''}
                        {o.aiOriginalQuantity != null && <span className="text-xs text-slate-400"> · AI {fmt(o.aiOriginalQuantity)}</span>}
                        {o.reviewedQuantity != null && <span className="text-emerald-600"> · reviewed {fmt(o.reviewedQuantity)}</span>}
                      </p>
                      {canReview && o.reviewStatus === 'PENDING_REVIEW' && (
                        correcting === o.id ? (
                          <div className="flex items-center gap-2 mt-2" data-testid="boq-correct-row">
                            <input type="number" defaultValue={o.observedQuantity} id={`corr-${o.id}`} className="w-24 px-2 py-1 border border-slate-300 rounded text-sm" data-testid="boq-corrected-input" />
                            <button onClick={() => review(o.id, 'CORRECTED', document.getElementById(`corr-${o.id}`).value, 'engineer correction')} className="text-xs px-2 py-1 bg-blue-600 text-white rounded" data-testid="boq-save-correction">Save</button>
                            <button onClick={() => setCorrecting(null)} className="text-xs text-slate-400">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 mt-2">
                            <button onClick={() => review(o.id, 'ACCEPTED')} className="text-xs text-emerald-600 flex items-center gap-1" data-testid="boq-accept-obs"><Check className="w-3 h-3" /> Accept</button>
                            <button onClick={() => setCorrecting(o.id)} className="text-xs text-blue-600 flex items-center gap-1" data-testid="boq-correct-obs"><Edit3 className="w-3 h-3" /> Correct</button>
                            <button onClick={() => review(o.id, 'REJECTED', null, 'rejected by reviewer')} className="text-xs text-red-500 flex items-center gap-1" data-testid="boq-reject-obs"><XCircle className="w-3 h-3" /> Reject</button>
                          </div>
                        )
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BOQItemDrawer;
